import type React from "react";
import { Box, Card, Separator, Skeleton } from "@salestrak/ui";

import { SETTINGS_CONTENT_MAX_WIDTH } from "#app/components/page-header";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// SettingsPageSkeleton — the first-paint stand-in for a settings page.
//
// Anti-flash contract: every bar is `delayed` (invisible for its first
// ~200ms), so a warm cache or the provider boot gap swaps real content in
// before any shimmer paints; only a genuinely slow load ever shows bars.
// The bars mirror the settled settings grammar — the SettingsSectionHeader
// label rung and SettingsGroupRow's 60px card rows — so real content lands
// with no reflow.
//
// Page chrome that is STATIC (a hardcoded PageHeader title) should render
// for real with this beside it; `header` is only for pages whose title is
// itself loaded data (a team or workflow name).
// ---------------------------------------------------------------------------

function SkeletonGroupRow() {
  return (
    <Box
      align="center"
      direction="row"
      gap="md"
      height={60}
      justify="between"
      paddingX="md"
    >
      <Box align="center" direction="row" gap="base">
        <Skeleton circle delayed width={24} />
        <Box direction="column" gap="xs">
          <Skeleton delayed height={12} width={140} />
          <Skeleton delayed height={10} width={220} />
        </Box>
      </Box>
      <Skeleton delayed height={12} width={56} />
    </Box>
  );
}

/**
 * Rows only, no card — for a list that loads INSIDE an existing
 * `SettingsGroup` (reasons, sources, API keys), standing in where the rows
 * will land. The group's own card and header stay real above it.
 */
export function SettingsGroupRowsSkeleton({
  rows = 3,
}: Omittable<{ rows?: number }>) {
  return (
    <>
      {Array.from({ length: rows }, (_, index) => (
        <Box key={index}>
          {index > 0 ? (
            <Box paddingX="md">
              <Separator />
            </Box>
          ) : null}
          <SkeletonGroupRow />
        </Box>
      ))}
    </>
  );
}

/** The SettingsGroupHeader rung: a count word leading, the list's own
 *  actions (collapsed search, plus, "Add Field") trailing, on the header's
 *  16px padding. */
function SkeletonGroupHeader() {
  return (
    <Box align="center" direction="row" justify="between" paddingX="md" paddingY="md">
      <Skeleton delayed height={12} width={64} />
      <Skeleton delayed height={24} width={56} />
    </Box>
  );
}

/**
 * Per-group stand-in shape, mirroring the settled page's card run so the
 * resolve swaps with no reflow. `unlabeled` for cards that render without a
 * SettingsSectionHeader; `subtitled` reserves the muted line under the label;
 * `header` draws the card's own SettingsGroupHeader rung (count + actions)
 * above the rows.
 */
export type SettingsSkeletonGroupShape = Omittable<{
  rows?: number;
  unlabeled?: boolean;
  subtitled?: boolean;
  header?: boolean;
}>;

function SkeletonGroup({
  rows,
  unlabeled = false,
  subtitled = false,
  header = false,
}: {
  rows: number;
  unlabeled?: boolean | undefined;
  subtitled?: boolean | undefined;
  header?: boolean | undefined;
}) {
  return (
    <Box direction="column" gap="md">
      {/* The section-label rung, on the row text rail (paddingX md). The bars
        * sit in boxes matching SettingsSectionHeader's real line heights
        * (16px base label, 14px sm subtitle) so the swap holds the rung. */}
      {!unlabeled ? (
        <Box direction="column" gap="xs" paddingX="md">
          <Box direction="column" height={24} justify="center">
            <Skeleton delayed height={16} width={96} />
          </Box>
          {subtitled ? (
            <Box direction="column" height={20} justify="center">
              <Skeleton delayed height={12} width={280} />
            </Box>
          ) : null}
        </Box>
      ) : null}
      <Card>
        {header ? <SkeletonGroupHeader /> : null}
        {Array.from({ length: rows }, (_, index) => (
          <Box key={index}>
            {index > 0 || header ? (
              <Box paddingX="md">
                <Separator />
              </Box>
            ) : null}
            <SkeletonGroupRow />
          </Box>
        ))}
      </Card>
    </Box>
  );
}

export function SettingsPageSkeleton({
  groups = 2,
  rowsPerGroup = 3,
  header = false,
  leading,
  gap = "2xl",
}: Omittable<{
  /** Section-card groups to draw: a count (uniform `rowsPerGroup` rows), or
   *  per-group shapes mirroring the settled page's card run. */
  groups?: number | SettingsSkeletonGroupShape[];
  rowsPerGroup?: number;
  /** Title/subtitle bars, for pages whose header text is loaded data. */
  header?: boolean;
  /** A page-specific stand-in that leads the column on the same 2xl rhythm
   *  as the groups — an identity card of inputs, say — so a page whose
   *  first card is not a row list still lands with no reflow. */
  leading?: React.ReactNode;
  /** The settled column's rhythm between cards: `2xl` for a page of labeled
   *  sections (the default), `lg` for a page whose cards stack unlabeled
   *  under its own title (Sources). */
  gap?: "lg" | "2xl";
}>) {
  const shapes: SettingsSkeletonGroupShape[] =
    typeof groups === "number"
      ? Array.from({ length: groups }, () => ({ rows: rowsPerGroup }))
      : groups;
  return (
    <Box align="center" direction="column" paddingX="md" paddingY="md">
      <Box
        direction="column"
        fill="width"
        gap={gap}
        maxWidth={SETTINGS_CONTENT_MAX_WIDTH}
      >
        {header ? (
          <Box direction="column" gap="sm" paddingX="md">
            <Skeleton delayed height={20} width={180} />
            <Skeleton delayed height={12} width={280} />
          </Box>
        ) : null}
        {leading}
        {shapes.map((shape, index) => (
          <SkeletonGroup
            key={index}
            header={shape.header}
            rows={shape.rows ?? rowsPerGroup}
            subtitled={shape.subtitled}
            unlabeled={shape.unlabeled}
          />
        ))}
      </Box>
    </Box>
  );
}
